// ── Export Service ──────────────────────────────────────────────────
// Client-seitiger Wrapper für Server-Side Excel + PDF Exporte via /api/export-report.
// Heavy Compute (FIFO, XLSX, PDF-Rendering) läuft serverseitig — kein Browser-Hang.
//
// Pure Daten-Builder leben in exportData.js (auch vom Server importiert),
// damit hier zusätzliche Browser-Imports (firebase Auth-Token) keine
// Server-Crashes auslösen.

import { getIdTokenSafe } from '../lib/tokenProvider';
import { LOCAL_COIN_ICONS_BY_ID } from './localCoinIcons';

// Re-Exports — Caller können weiterhin aus exportService importieren
export {
  buildYearExportData,
  buildAssetExportData,
  getAvailableYears,
} from './exportData';

// Coins ohne CoinGecko-`image`-Feld (z.B. BUSD seit Paxos-Einstellung) mit
// lokalem Fallback-Logo anreichern, BEVOR marketData an den Export-Server
// geht. Server-seitiges React-PDF braucht absolute URLs — deshalb Origin
// vorhängen.
function enrichMarketDataWithLocalIcons(marketData) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  if (!origin) return marketData || {};
  const out = { ...(marketData || {}) };
  for (const [cgId, relPath] of Object.entries(LOCAL_COIN_ICONS_BY_ID)) {
    const existing = out[cgId];
    if (!existing?.image) {
      out[cgId] = { ...(existing || {}), image: `${origin}${relPath}` };
    }
  }
  return out;
}

// ── Server-Side Export via /api/export-report ──────────────────────────────
// Native: (1) absolute URL — relative /api/... löst auf capacitor://localhost (kein
// Server). (2) Binärdaten über CapacitorHttp (responseType blob → base64), weil der
// fetch-Shim Binär verstümmelt. (3) Speichern/Teilen via Filesystem+Share, weil der
// WKWebView keinen Blob-Anchor-Download kann. Web: unverändert fetch+blob+Anchor.
const _NATIVE = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
const _API_BASE = _NATIVE ? 'https://app.redcrypto.de' : '';

function _filenameFromCD(cd, format) {
  const m = (cd || '').match(/filename\s*=\s*"?([^";]+)"?/i);
  return m?.[1] || `red-export-${Date.now()}.${format}`;
}

async function postExport(payload) {
  let idToken = null;
  try {
    idToken = await getIdTokenSafe();
  } catch { /* ohne Token: Server antwortet 401 falls Auth required */ }
  const headers = {
    'Content-Type': 'application/json',
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
  };
  const url = `${_API_BASE}/api/export-report`;

  if (_NATIVE) {
    const { CapacitorHttp } = await import('@capacitor/core');
    const resp = await CapacitorHttp.request({ method: 'POST', url, headers, data: payload, responseType: 'blob' });
    if (resp.status >= 400) {
      let msg = '';
      try { const j = typeof resp.data === 'string' ? JSON.parse(atob(resp.data)) : resp.data; msg = j?.detail || j?.error || ''; } catch {}
      throw new Error(msg || `Export fehlgeschlagen (${resp.status}).`);
    }
    const cd = resp.headers?.['Content-Disposition'] || resp.headers?.['content-disposition'] || '';
    return { base64: resp.data, filename: _filenameFromCD(cd, payload.format), native: true };
  }

  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
  if (!res.ok) {
    let msg = '';
    try {
      const j = await res.json();
      msg = j?.detail || j?.error || '';
      if (j?.stack) console.error('[export] server stack:', j.stack);
    } catch {}
    throw new Error(msg || `Export fehlgeschlagen (${res.status}).`);
  }
  const cd = res.headers.get('Content-Disposition') || '';
  const blob = await res.blob();
  return { blob, filename: _filenameFromCD(cd, payload.format) };
}

// Native: Datei in den Cache schreiben + System-Share-Sheet (Sichern in Dateien,
// Mail, …). Web: klassischer Blob-Anchor-Download.
async function deliverExport(result) {
  if (result.native) {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const { Share } = await import('@capacitor/share');
    await Filesystem.writeFile({ path: result.filename, data: result.base64, directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path: result.filename, directory: Directory.Cache });
    await Share.share({ title: result.filename, url: uri });
    return;
  }
  const url = URL.createObjectURL(result.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export async function exportYearReport({ format, txs, year, isDE, country, marketData }) {
  const result = await postExport({
    scope: 'year', format, txs, year, isDE, country,
    marketData: enrichMarketDataWithLocalIcons(marketData),
  });
  await deliverExport(result);
}

// Downsample auf <= maxPts Punkte (gleichmaeßiges Stride). Haelt den Request
// klein (Vercel Body-Limit 4,5 MB) — der PDF-Chart braucht keine Vollauflösung.
function downsampleSeries(series, maxPts = 120) {
  if (!Array.isArray(series) || series.length <= maxPts) return series || [];
  const stride = Math.ceil(series.length / maxPts);
  const out = [];
  for (let i = 0; i < series.length; i += stride) out.push(series[i]);
  // Letzten Punkt immer mitnehmen (aktueller Kurs)
  if (out[out.length - 1] !== series[series.length - 1]) out.push(series[series.length - 1]);
  return out;
}

export async function exportAssetReport({ format, asset, txs, isDE, country, marketData, priceSeries }) {
  const result = await postExport({
    scope: 'asset', format, asset, txs, isDE, country,
    marketData: enrichMarketDataWithLocalIcons(marketData),
    // Kursverlauf fuer den PDF-Chart — nur Client hat die Historie. Optional:
    // fehlt sie, laesst der PDF-Builder den Chart einfach weg.
    priceSeries: priceSeries ? downsampleSeries(priceSeries) : undefined,
  });
  await deliverExport(result);
}
